import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/heroSlides';

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const total = heroSlides.length;

  const go = useCallback((next: number) => {
    setIndex((next + total) % total);
  }, [total]);

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % total), 6000);
    return () => clearInterval(timer);
  }, [total]);

  return (
    <section className="relative w-full h-[520px] md:h-[640px] lg:h-[720px] overflow-hidden bg-primary-950">
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <img
            src={slide.backdrop}
            alt={`${slide.headline} 背景剧照`}
            title={`${slide.headline} - 久久热推荐`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/50 to-primary-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/70 via-transparent to-primary-950/20" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 h-full w-full px-4 md:px-8">
        <div className="h-full flex items-center">
          <div className="max-w-2xl">
            {heroSlides.map((slide, i) => (
              i === index && (
                <div key={slide.id} className="animate-fade-up">
                  <span className="inline-flex items-center gap-2 h-7 px-3 rounded-full bg-accent-500/90 text-accent-950 text-xs font-semibold tracking-wide">
                    <i className="ri-fire-fill text-xs" />
                    {slide.kicker}
                  </span>
                  <h1 className="mt-4 font-display text-4xl md:text-6xl font-extrabold text-background-50 leading-tight">
                    {slide.headline}
                  </h1>
                  <p className="mt-4 text-base md:text-lg text-background-100/90 max-w-xl leading-relaxed">
                    {slide.description}
                  </p>
                  <div className="mt-7 flex flex-wrap items-center gap-3">
                    <Link
                      to={`/detail/${slide.movieId}`}
                      className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-information-line text-lg" />
                      查看详情
                    </Link>
                    <Link
                      to="/category/movie"
                      className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/10 border border-background-100/40 text-background-50 text-sm font-semibold hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap backdrop-blur"
                    >
                      <i className="ri-compass-3-line text-lg" />
                      逛逛影视库
                    </Link>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      </div>

      {/* Arrows */}
      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-background-50/10 border border-background-100/30 text-background-50 items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer backdrop-blur"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-background-50/10 border border-background-100/30 text-background-50 items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer backdrop-blur"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {heroSlides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`切换到第${i + 1}张`}
            onClick={() => go(i)}
            className={`h-2 rounded-full transition-all cursor-pointer ${i === index ? 'w-8 bg-accent-500' : 'w-2 bg-background-100/50 hover:bg-background-100/80'}`}
          />
        ))}
      </div>
    </section>
  );
}